import type { ProductionRequirementView } from '@scoutaround/script-breakdown-shared';

import {
  ASSUMPTION_LABELS,
  BASIS_LABELS,
  EVENT_TYPE_LABELS,
  INTERPRETATION_SOURCE_LABELS,
  STATE_LABELS,
  interpretationText,
} from './labels.js';
import { usePlan } from './context.js';
import { EvidenceQuotes } from './PlanParts.js';

/**
 * HOW THE PLAN GOT HERE, shown when asked for.
 *
 * The explanation in words, the rules that produced it (`ID@version`), and the
 * facts it rests on — the events the screenplay shows, the tagged elements, the
 * scenes, the project assumptions — each kept as what it is. An interpretation
 * (a hazard, a scale) is labelled as one, with the model or the rule that made
 * it, and never reads as something the screenplay says.
 */
export function DerivationInspector({
  requirement,
  sceneId,
}: {
  requirement: ProductionRequirementView;
  /** The scene the row is shown on; an interpretation's basis from it can be shown in the reader. */
  sceneId?: string;
}) {
  const { plan, sceneLabel } = usePlan();
  const from = requirement.derivedFrom;
  const events = (plan?.events ?? []).filter((e) => from.eventLineageIds.includes(e.lineageId));
  const assumptions = from.assumptionKeys.map((key) => ({
    key,
    value: plan?.assumptions.find((a) => a.key === key)?.value ?? null,
  }));
  const id = requirement.id;

  return (
    <div className="pp-derivation" data-testid={`production-derivation-${id}`}>
      <p className="pp-derivation-text">{requirement.explanation === '' ? 'No explanation recorded.' : requirement.explanation}</p>
      <p className="mm pp-note" data-testid={`production-derivation-basis-${id}`}>
        {BASIS_LABELS[requirement.basisStatus]}
      </p>

      {requirement.rules.length === 0 ? null : (
        <div className="pp-kv">
          <span className="pp-kv-label">Rules</span>
          <span className="pp-codes" data-testid={`production-derivation-rules-${id}`}>
            {requirement.rules.map((rule) => (
              <code key={rule}>{rule}</code>
            ))}
          </span>
        </div>
      )}

      <div className="pp-kv">
        <span className="pp-kv-label">It rests on</span>
        <ul className="pp-plain-list" data-testid={`production-derivation-facts-${id}`}>
          {events.map((event) => (
            <li key={event.id}>
              {`Observation: ${EVENT_TYPE_LABELS[event.type].toLowerCase()} — ${event.title} (${sceneLabel(event.sceneId)})`}
              {event.observations.statedAge === null
                ? null
                : `; stated age ${String(event.observations.statedAge.years)}`}
              {event.observations.state === null
                ? null
                : `; ${event.observations.state.subject} ${STATE_LABELS[event.observations.state.state]}`}
            </li>
          ))}
          {from.mentionIds.length === 0 ? null : (
            <li>{`${String(from.mentionIds.length)} tagged element${from.mentionIds.length === 1 ? '' : 's'} in the screenplay`}</li>
          )}
          {from.sceneIds.length === 0 ? null : <li>{`Scenes: ${from.sceneIds.map(sceneLabel).join('; ')}`}</li>}
          {assumptions.map((a) => (
            <li key={a.key}>{`Assumption: ${ASSUMPTION_LABELS[a.key]} — ${a.value ?? 'Not set'}`}</li>
          ))}
          {events.length === 0 && from.mentionIds.length === 0 && from.sceneIds.length === 0 && assumptions.length === 0 ? (
            <li>Nothing recorded: it was added by hand.</li>
          ) : null}
        </ul>
      </div>

      {events.some((e) => e.interpretations.length > 0) ? (
        <div className="pp-kv">
          <span className="pp-kv-label">Interpretations</span>
          <ul className="pp-plain-list" data-testid={`production-derivation-interpretations-${id}`}>
            {events.flatMap((event) =>
              event.interpretations.map((interpretation, index) => (
                <li key={`${event.id}-${interpretation.key}-${String(index)}`} className="pp-interpretation">
                  <span className="pp-interpretation-tag">{`Interpretation (${INTERPRETATION_SOURCE_LABELS[interpretation.source]})`}</span>
                  {`: ${interpretationText(interpretation.key, interpretation.value)} — ${event.title}`}
                  {interpretation.basis === null ? null : (
                    <EvidenceQuotes
                      spans={[interpretation.basis]}
                      sceneId={sceneId}
                      testId={`production-derivation-quotes-${id}-${event.id}-${String(index)}`}
                    />
                  )}
                </li>
              )),
            )}
          </ul>
        </div>
      ) : null}

      {requirement.provenance.model === null ? null : (
        <p className="mm pp-note">
          {`Read by ${requirement.provenance.model}${requirement.provenance.promptVersion === null ? '' : `, prompt ${requirement.provenance.promptVersion}`}.`}
        </p>
      )}
    </div>
  );
}
